<template>
  <div
    id="customer-details"
    class="mb-4"
  >
    <div class="col-heading">
      <h3 class="mb-4">
        Enter customer details
      </h3>
    </div>

    <div class="form-group col-md-12 col-lg-10 col-xl-9 section__group">
      <form>
        <div class="row">
          <div class="form-group mb-4 col-6">
            <label>
              First name
            </label>
            <input
              v-model="firstName"
              :disabled="disabled"
              class="form-control"
              type="text"
            >
            <small
              v-if="$v.firstName.$invalid && $v.firstName.$dirty"
              class="text--error"
            >Please enter a valid first name</small>
          </div>

          <div class="form-group mb-4 col-6">
            <label>
              Last name
            </label>
            <input
              v-model="surname"
              :disabled="disabled"
              class="form-control"
              type="text"
            >
            <small
              v-if="$v.surname.$invalid && $v.surname.$dirty"
              class="text--error"
            >Please enter a valid surname</small>
          </div>
        </div>

        <div class="row">
          <div class="form-group mb-4 col-6">
            <label>
              Email address
            </label>
            <input
              v-model="email"
              :disabled="disabled"
              class="form-control"
              type="email"
              aria-describedby="emailHelp"
            >
            <small
              v-if="$v.email.$invalid && $v.email.$dirty"
              class="text--error"
            >Please enter a valid email address</small>
          </div>

          <div class="form-group mb-4 col-6">
            <label>
              Mobile phone (optional)
            </label>
            <input
              v-model="mobilePhone"
              :disabled="disabled"
              class="form-control"
              type="text"
            >
            <small
              v-if="$v.mobilePhone.$invalid && $v.mobilePhone.$dirty"
              class="text--error"
            >Please enter a valid phone number</small>
          </div>
        </div>

        <div v-if="selectedAirportCode === 'BNE' && this.checkForValet()" class="row">
          <div class="form-group mb-4 col-6">
            <label>
              Outbound flight
            </label>
            <input
              v-model="outboundFlight"
              :disabled="disabled"
              class="form-control"
              type="text"
            >
            <small
              v-if="$v.outboundFlight.$invalid && $v.outboundFlight.$dirty"
              class="text--error"
            >Please enter the outbound flight number</small>
          </div>

          <div class="form-group mb-4 col-6">
            <label>
              Inbound flight
            </label>
            <input
              v-model="inboundFlight"
              :disabled="disabled"
              class="form-control"
              type="text"
            >
            <small
              v-if="$v.inboundFlight.$invalid && $v.inboundFlight.$dirty"
              class="text--error"
            >Please enter the inbound flight number</small>
          </div>
        </div>

        <div class="field field--terms">
          <span class="proxy-label field__label">Marketing communications</span>
          <div class="input__checkbox-label mb-1">
            <input
              id="AllowContact"
              v-model="hasRequestedMarketing"
              :disabled="disabled"
              type="checkbox"
              class="input__checkbox"
            >
            <label
              for="AllowContact"
              class="input__checkbox-input"
            >
              Customer agrees to being contacted by {{ getSelectedAirport.name }} regarding airport discounts, competitions, services and announcements.
            </label>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { required, email, numeric, minLength, requiredIf } from 'vuelidate/lib/validators'
export default {
    name: 'CustomerDetails',
    props: {
        disabled: {
            type: Boolean,
            default: false
        },
        selectedAirportCode: {
            type: String,
            default: ''
        },
        booking: {
            type: Object,
            default: null
        }
    },
    data () {
        return {
        }
    },
    validations: {
        firstName: {
            required: requiredIf(function () {
                return this.selectedAirportCode !== 'BNE'
            })
        },
        surname: {
            required: requiredIf(function () {
                return this.selectedAirportCode !== 'BNE'
            })
        },
        email: {
            required,
            email
        },
        mobilePhone: {
            numeric,
            minLength: minLength(5)
        },
        outboundFlight: {
            required: requiredIf(function () {
                return this.selectedAirportCode === 'BNE' && this.checkForValet()
            })
        },
        inboundFlight: {
            required: requiredIf(function () {
                return this.selectedAirportCode === 'BNE' && this.checkForValet()
            })
        }
    },
    computed: {
        firstName: {
            get () {
                return this.$store.state.booking.customer.firstName
            },
            set (value) {
                this.$v.firstName.$touch()
                this.$store.commit('setCustomerFirstName', value)
                this.validateForm()
            }
        },
        surname: {
            get () {
                return this.$store.state.booking.customer.surname
            },
            set (value) {
                this.$v.surname.$touch()
                this.$store.commit('setCustomerSurname', value)
                this.validateForm()
            }
        },
        email: {
            get () {
                return this.$store.state.booking.customer.email
            },
            set (value) {
                this.$v.email.$touch()
                this.$store.commit('setCustomerEmail', value)
                this.validateForm()
            }
        },
        mobilePhone: {
            get () {
                return this.$store.state.booking.customer.mobilePhone
            },
            set (value) {
                this.$v.mobilePhone.$touch()
                this.$store.commit('setCustomerMobilePhone', value)
                this.validateForm()
            }
        },
        outboundFlight: {
            get () {
                return this.$store.state.booking.customer.outboundFlight
            },
            set (value) {
                this.$v.outboundFlight.$touch()
                this.$store.commit('setCustomerOutboundFlight', value)
                this.validateForm()
            }
        },
        inboundFlight: {
            get () {
                return this.$store.state.booking.customer.inboundFlight
            },
            set (value) {
                this.$v.inboundFlight.$touch()
                this.$store.commit('setCustomerInboundFlight', value)
                this.validateForm()
            }
        },
        hasRequestedMarketing: {
            get () {
                return this.$store.state.booking.customer.hasRequestedMarketing
            },
            set (value) {
                this.$store.commit('setCustomerHasRequestedMarketing', value)
            }
        },
        ...mapGetters(['getSelectedAirport'])
    },
    mounted () {
        this.$v.$touch()
        this.validateForm()
    },
    methods: {
        validateForm () {
            if (this.$store.state.booking.newBookingFormValid === true || !this.$v.$invalid) this.$store.commit('setNewBookingFormValid', !this.$v.$invalid)
        },
        checkForValet () {
            // this would be more reliable if we had a specific product type to check against.
            return (this.booking.name.toLowerCase().includes('valet') || this.booking.description.toLowerCase().includes('valet'))
        }
    }
}
</script>
